import { useEffect, useState } from "react";
import { GetBoxPhoto } from "@/Utils/DataLib";
import { GetDoc } from "@/Utils/StateLib";
import { kMissingPic } from "@coreapp/constants";
import { GetPicSource } from "@coreapp/utils/GetPicSource";
import { getBase64Image } from "@coreapp/utils/getBase64Image";

type LoadedPhoto = { src: string; width: number; height: number };

const loadPhoto = (photoValue: unknown, onLoaded: (photo: LoadedPhoto) => void) => {
  const report = (image: any) => {
    if (!image?.width || !image.height) {
      return;
    }
    if (!image.canvasImgUrl) {
      image.canvasImgUrl = getBase64Image(image);
    }
    if (image.canvasImgUrl) {
      onLoaded({ src: image.canvasImgUrl, width: image.width, height: image.height });
    }
  };

  const showLoadedPhoto = () => {
    const doc = GetDoc();
    if (!doc) {
      return;
    }
    const picDesc = GetPicSource(null, null, photoValue);
    report(doc.FindPicture(picDesc.fDirectory, picDesc.fFileName));
  };

  report(
    GetBoxPhoto(
      null,
      null,
      photoValue,
      { ImageLoaded: showLoadedPhoto, SubscriberImageLoaded: showLoadedPhoto },
      true
    )
  );
};

const isFailedLoad = (photo: LoadedPhoto, onResult: (failed: boolean) => void) => {
  if (photo.width <= 1 || photo.height <= 1) {
    onResult(true);
    return;
  }
  loadPhoto(kMissingPic, missing => onResult(missing.src === photo.src));
};

export const useRecordPhotoSrc = (
  photoFieldValue: unknown,
  fallbackValue?: string
): string | undefined => {
  const [photoSrc, setPhotoSrc] = useState<string | undefined>(undefined);

  useEffect(() => {
    setPhotoSrc(undefined);

    let isCurrent = true;
    const show = (photo: LoadedPhoto) => {
      if (isCurrent) {
        setPhotoSrc(photo.src);
      }
    };
    const showFallback = () => {
      if (isCurrent && fallbackValue) {
        loadPhoto(fallbackValue, show);
      }
    };

    if (!photoFieldValue) {
      showFallback();
    } else {
      loadPhoto(photoFieldValue, photo => {
        if (!isCurrent) {
          return;
        }
        if (!fallbackValue) {
          show(photo);
          return;
        }
        isFailedLoad(photo, failed => (failed ? showFallback() : show(photo)));
      });
    }

    return () => {
      isCurrent = false;
    };
  }, [photoFieldValue, fallbackValue]);

  return photoSrc;
};
